import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Users, Heart, Shield, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const CommunityGuidelines = ({ onBack }) => {
  const guidelines = [
    {
      title: 'Be Authentic',
      icon: CheckCircle,
      color: 'text-green-500',
      do: [
        'Use current, accurate photos of yourself',
        'Provide truthful information in your profile',
        'Be genuine in your conversations',
        'Represent yourself honestly'
      ],
      dont: [
        'Use fake or heavily filtered photos',
        'Lie about your age, location, or relationship status',
        'Create multiple accounts',
        'Impersonate someone else'
      ]
    },
    {
      title: 'Treat Others with Respect',
      icon: Heart,
      color: 'text-red-500',
      do: [
        'Be kind and courteous in all interactions',
        'Respect when someone says no or isn\'t interested',
        'Use appropriate language and tone',
        'Celebrate diversity and different perspectives'
      ],
      dont: [
        'Send inappropriate, offensive, or explicit content',
        'Harass, bully, or threaten other users',
        'Use discriminatory or hateful language',
        'Pressure someone who has declined your interest'
      ]
    },
    {
      title: 'Keep It Safe',
      icon: Shield,
      color: 'text-blue-500',
      do: [
        'Report suspicious or inappropriate behavior',
        'Protect your personal information',
        'Meet in public places for first dates',
        'Trust your instincts about other users'
      ],
      dont: [
        'Share personal details like your address or workplace',
        'Send money or financial information',
        'Engage with users who make you uncomfortable',
        'Ignore red flags or concerning behavior'
      ]
    },
    {
      title: 'Follow Platform Rules',
      icon: Users,
      color: 'text-purple-500',
      do: [
        'Use FlameLink for its intended purpose - dating',
        'Respect intellectual property and copyrights',
        'Follow all applicable laws and regulations',
        'Keep conversations appropriate and consensual'
      ],
      dont: [
        'Use the platform for commercial purposes or advertising',
        'Spam other users with unwanted messages',
        'Share copyrighted content without permission',
        'Violate any local or international laws'
      ]
    }
  ];

  const violations = [
    {
      severity: 'Minor Violations',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      examples: ['Inappropriate language', 'Spam messaging', 'Minor profile inaccuracies'],
      consequences: ['Warning message', 'Temporary message restrictions', 'Profile review']
    },
    {
      severity: 'Serious Violations',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      examples: ['Harassment', 'Fake profiles', 'Sharing inappropriate content'],
      consequences: ['Account suspension', 'Feature restrictions', 'Profile removal']
    },
    {
      severity: 'Severe Violations',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      examples: ['Threats of violence', 'Illegal content', 'Persistent harassment'],
      consequences: ['Permanent account ban', 'Law enforcement reporting', 'IP address blocking']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent">
              Community Guidelines
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Building a respectful, safe, and authentic dating community for everyone. These guidelines help create 
            positive experiences for all FlameLink users.
          </p>
        </div>

        {/* Our Values */}
        <Card className="p-8 mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Heart className="w-6 h-6 text-red-500 mr-3" />
            Our Community Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Authenticity</h3>
              <p className="text-gray-600 text-sm">Be genuine and honest in all your interactions</p>
            </div>
            <div className="text-center">
              <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Respect</h3>
              <p className="text-gray-600 text-sm">Treat every person with kindness and consideration</p>
            </div>
            <div className="text-center">
              <Shield className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Safety</h3>
              <p className="text-gray-600 text-sm">Create a secure environment for meaningful connections</p>
            </div>
          </div>
        </Card>

        {/* Guidelines */}
        <div className="space-y-8">
          {guidelines.map((guideline, index) => {
            const Icon = guideline.icon;
            return (
              <Card key={index} className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Icon className={`w-6 h-6 ${guideline.color} mr-3`} />
                  {guideline.title}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Do's */}
                  <div>
                    <h3 className="text-lg font-semibold text-green-600 mb-4 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Do's
                    </h3>
                    <div className="space-y-3">
                      {guideline.do.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Don'ts */}
                  <div>
                    <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                      <X className="w-5 h-5 mr-2" />
                      Don'ts
                    </h3>
                    <div className="space-y-3">
                      {guideline.dont.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start">
                          <X className="w-4 h-4 text-red-500 mt-1 mr-3 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Violation Consequences */}
        <Card className="p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <AlertTriangle className="w-6 h-6 text-orange-500 mr-3" />
            Violation Consequences
          </h2>
          <p className="text-gray-600 mb-6">
            We take community guideline violations seriously. Depending on the severity and frequency of violations, 
            we may take the following actions:
          </p>
          
          <div className="space-y-6">
            {violations.map((violation, index) => (
              <div key={index} className={`p-6 rounded-lg ${violation.bgColor} border ${violation.borderColor}`}>
                <h3 className={`text-lg font-semibold ${violation.color} mb-3`}>{violation.severity}</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Examples:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      {violation.examples.map((example, exampleIndex) => (
                        <li key={exampleIndex}>• {example}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Consequences:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      {violation.consequences.map((consequence, consequenceIndex) => (
                        <li key={consequenceIndex}>• {consequence}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Reporting */}
        <Card className="p-8 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Reporting Violations</h2>
          <p className="text-gray-600 mb-6">
            If you encounter behavior that violates our community guidelines, please report it immediately. 
            We review all reports promptly and take appropriate action.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How to Report:</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Use the report button on any profile or message</li>
                <li>• Email our safety team directly</li>
                <li>• Contact support through the app</li>
                <li>• Use our online reporting form</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">What Happens Next:</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• We review your report within 24 hours</li>
                <li>• Investigation begins immediately</li>
                <li>• Appropriate action is taken</li>
                <li>• You'll receive a follow-up if needed</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Contact */}
        <div className="text-center mt-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Our Guidelines?</h3>
          <p className="text-gray-600 mb-6">
            We're here to help create a positive community experience for everyone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.open('mailto:community@flamelink.com', '_blank')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            >
              Contact Community Team
            </Button>
            <Button
              variant="outline"
              onClick={onBack}
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;